import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TeamRecentGame {
  fixture_id: number;
  match_date: string;
  
  // Complete data for both teams
  home_team_name: string;
  home_team_logo: string;
  home_team_tla: string;
  home_score: number;
  
  away_team_name: string;
  away_team_logo: string;
  away_team_tla: string;
  away_score: number;
  
  // Legacy fields for backward compatibility
  opponent_name: string;
  opponent_logo: string;
  score_for: number;
  score_against: number;
  result: 'W' | 'D' | 'L';
  location: 'home' | 'away';
  league_name: string;
  venue: string | null;
}

interface UseTeamRecentGamesProps {
  teamId: string | null;
  location?: 'home' | 'away' | 'all';
  limit?: number;
}

// Helper to generate Three Letter Abbreviation
const generateTLA = (teamName: string): string => {
  const cleanName = teamName
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z]/g, '')
    .toUpperCase();
  return cleanName.substring(0, 3) || 'UNK';
};

export const useTeamRecentGames = ({
  teamId,
  location = 'all',
  limit = 5
}: UseTeamRecentGamesProps) => {
  return useQuery({
    queryKey: ['team-recent-games', teamId, location, limit],
    queryFn: async () => {
      if (!teamId) return [];

      let query = supabase
        .from('games')
        .select(`
          fixture_id,
          match_date,
          home_score,
          away_score,
          status,
          venue,
          home_team_id,
          away_team_id,
          home_team:teams!home_team_id(name, logo_url),
          away_team:teams!away_team_id(name, logo_url),
          league:leagues(name)
        `)
        .eq('status', 'finished')
        .order('match_date', { ascending: false })
        .limit(limit * 2); // Fetch more to ensure we get enough after filtering

      // Apply location filter
      if (location === 'home') {
        query = query.eq('home_team_id', teamId);
      } else if (location === 'away') {
        query = query.eq('away_team_id', teamId);
      } else {
        query = query.or(`home_team_id.eq.${teamId},away_team_id.eq.${teamId}`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching team recent games:', error);
        throw error;
      }

      const games: TeamRecentGame[] = (data || []).slice(0, limit).map((game: any) => {
        const isHome = game.home_team_id === teamId;
        const scoreFor = isHome ? game.home_score : game.away_score;
        const scoreAgainst = isHome ? game.away_score : game.home_score;
        
        let result: 'W' | 'D' | 'L';
        if (scoreFor > scoreAgainst) result = 'W';
        else if (scoreFor < scoreAgainst) result = 'L';
        else result = 'D';

        return {
          fixture_id: game.fixture_id,
          match_date: game.match_date,
          
          // Complete data for both teams
          home_team_name: game.home_team?.name || 'Unknown',
          home_team_logo: game.home_team?.logo_url || '',
          home_team_tla: generateTLA(game.home_team?.name || 'UNK'),
          home_score: game.home_score || 0,
          
          away_team_name: game.away_team?.name || 'Unknown',
          away_team_logo: game.away_team?.logo_url || '',
          away_team_tla: generateTLA(game.away_team?.name || 'UNK'),
          away_score: game.away_score || 0,
          
          // Legacy fields for backward compatibility
          opponent_name: isHome ? game.away_team?.name : game.home_team?.name,
          opponent_logo: isHome ? game.away_team?.logo_url : game.home_team?.logo_url,
          score_for: scoreFor || 0,
          score_against: scoreAgainst || 0,
          result,
          location: isHome ? 'home' : 'away',
          league_name: game.league?.name || '',
          venue: game.venue
        };
      });

      return games;
    },
    enabled: !!teamId,
    staleTime: 300000, // 5 minutes
  });
};

// Helper to calculate stats from recent games
export const calculateRecentStats = (games: TeamRecentGame[]) => {
  const wins = games.filter(g => g.result === 'W').length;
  const draws = games.filter(g => g.result === 'D').length;
  const losses = games.filter(g => g.result === 'L').length;
  const goalsFor = games.reduce((sum, g) => sum + g.score_for, 0);
  const goalsAgainst = games.reduce((sum, g) => sum + g.score_against, 0);
  const form = games.slice(0, 5).map(g => g.result).join('-');

  return {
    wins,
    draws,
    losses,
    goalsFor,
    goalsAgainst,
    form
  };
};
